import type { FC } from "react";
import { memo, useCallback } from "react";

import type { SelectOptionType } from "@ui/components/DEPRECATED_CustomSelect/selectOptions";
import { Controller, useFormContext } from "@ui/components/Form";
import type { PlaceInputResults } from "@ui/components/PlaceInput/PlaceInput";
import { PlaceInput } from "@ui/components/PlaceInput/PlaceInput";
import { useTranslations } from "@ui/providers/useTranslations";
import { typedEntries } from "@ui/utils/object";
import classNames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import { SearchDefaultIcon } from "@jti/brand-icons/search-default";
import type { LocationServiceAutocompleteService } from "@jti/fe-common";
import type { IDeliveryLimits } from "@jti/magento";

import { InputIcon, TextInput } from "../Inputs";

import styles from "./StreetAddressFields.module.scss";

export type StreetAddressFieldsProps = {
  fieldName?(name: string): string;
  enableStreetNameAutocomplete?: boolean;
  onUpdateStreet: (address: PlaceInputResults) => void;
  required?: boolean;
  locationServiceAutocompleteProvider?: LocationServiceAutocompleteService;
  isSingleFieldAddressLookup?: boolean;
  isStreetFieldCombined?: boolean;
  ignoredAutoUpdates?: (keyof PlaceInputResults)[];
};

export function getDeliveryLimitCitiesDropdownList(citiesList: IDeliveryLimits[] = []): SelectOptionType[] {
  return citiesList.map((cityEntity) => ({
    label: cityEntity.name,
    value: cityEntity.name,
  }));
}

export const StreetAddressFields: FC<StreetAddressFieldsProps> = memo(
  ({
    fieldName = (name: string) => name,
    enableStreetNameAutocomplete,
    onUpdateStreet,
    required = true,
    locationServiceAutocompleteProvider,
    isSingleFieldAddressLookup = false,
    isStreetFieldCombined = false,
    ignoredAutoUpdates = ["regionCode"],
  }) => {
    const { __ } = useTranslations();
    const { addressFormVariant } = useConfig();
    const form = useFormContext();

    // TODO: fix useSyncFieldLabels and new inputs to work together DESP-92197
    // const streetNameFieldRef = useRef<HTMLDivElement>(null);
    // const streetNumberFieldRef = useRef<HTMLDivElement>(null);

    // const isScreenAboveMobile = useMediaQuery({ minWidth: BREAKPOINT_MEDIUM });
    // useSyncFieldsLabels([streetNameFieldRef, streetNumberFieldRef], { disableSync: !isScreenAboveMobile });

    const isReversedStreetAddress =
      addressFormVariant === "standard_reversed" ||
      addressFormVariant === "extended_asian_reversed" ||
      addressFormVariant === "extended_with_province_reversed" ||
      addressFormVariant === "with_regions_reversed";

    const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        // to prevent form submission on selecting a place from autocomplete dropdown using Enter key
        event.preventDefault();
      }
    }, []);

    const handlePlace = useCallback(
      (place: PlaceInputResults) => {
        typedEntries(place).forEach(([key, value]) => {
          if (!value) {
            return;
          }

          if (ignoredAutoUpdates?.includes(key)) {
            return;
          }

          if (isStreetFieldCombined && (key === "streetName" || key === "streetNumber")) {
            if (key === "streetName") {
              const combinedStreet = isReversedStreetAddress
                ? [place.streetNumber, place.streetName]
                : [place.streetName, place.streetNumber];

              form.setValue(fieldName("streetName"), combinedStreet.filter(Boolean).join(" "));
            }
            return;
          }

          form.setValue(fieldName(key), value);
        });

        onUpdateStreet(place);
      },
      [fieldName, form, isReversedStreetAddress, isStreetFieldCombined, onUpdateStreet]
    );

    const renderStreetFields = () => {
      const streetName = (
        <Controller
          control={form.control}
          name={fieldName("streetName")}
          rules={{ required: required ? __("form_validation.field_required") : false }}
          render={({ field, fieldState }) => (
            <PlaceInput
              {...field}
              label={__("address_fields.street_name")}
              labelId="label-street-name-field"
              placeholder={__("address_fields.street_name_placeholder")}
              onPlace={handlePlace}
              onKeyDown={handleKeyDown}
              isError={!!fieldState.error}
              helperText={fieldState.error?.message}
              required={required}
              containerClassName={styles.input}
              data-testid="streetName"
              autoComplete="address-line1"
              autocompleteProvider={
                enableStreetNameAutocomplete && !isSingleFieldAddressLookup
                  ? locationServiceAutocompleteProvider
                  : "none"
              }
            />
          )}
        />
      );
      const streetNumber = (
        <Controller
          control={form.control}
          key="streetNumber"
          name={fieldName("streetNumber")}
          rules={{
            required: { value: required, message: __("form_validation.field_required") },
          }}
          render={({ field, fieldState }) => (
            <TextInput
              {...field}
              label={__("address_fields.street_number")}
              data-testid="streetNumber"
              placeholder={__("address_fields.street_number_placeholder")}
              labelId="label-street-number-field"
              required={required}
              containerClassName={styles.input}
              isError={!!fieldState.error}
              helperText={fieldState.error?.message}
              autoComplete="address-line2"
            />
          )}
        />
      );

      if (isStreetFieldCombined) {
        return streetName;
      }

      const componentsArr = [streetName, streetNumber];

      return isReversedStreetAddress ? componentsArr.reverse() : componentsArr;
    };

    const renderStreetAdditionalField = () => (
      <Controller
        name={fieldName("streetAdditional")}
        control={form.control}
        render={({ field }) => (
          <TextInput
            {...field}
            label={__("address_fields.street_additional")}
            labelId="label-street-additional-field"
            data-testid="streetAdditional"
            aria-labelledby="label-street-additional-field"
            placeholder={__("address_fields.street_additional_placeholder")}
            containerClassName={styles.input}
            autoComplete="address-line3"
          />
        )}
        rules={{ required: false }}
      />
    );

    return (
      <div className={styles.addressFields}>
        {isSingleFieldAddressLookup && (
          <Controller
            key="singleFieldAddress"
            control={form.control}
            name={fieldName("singleFieldAddress")}
            rules={{ required: false }}
            render={({ field }) => (
              <PlaceInput
                {...field}
                label={__("address_fields.single_field_address")}
                labelId="label-single-field-address"
                placeholder={__("address_fields.single_field_address_placeholder")}
                onPlace={handlePlace}
                containerClassName={styles.input}
                data-testid="singleFieldAddress"
                autocompleteProvider={locationServiceAutocompleteProvider}
                aria-label="single-field-address"
                elemBefore={<InputIcon icon={SearchDefaultIcon} />}
                autoComplete="street-address"
              />
            )}
          />
        )}
        {isStreetFieldCombined ? (
          <div className={classNames(styles.addressFields, styles.innerWrapper)}>
            {renderStreetFields()}
            {renderStreetAdditionalField()}
          </div>
        ) : (
          <>
            <div className={classNames(styles.addressFields, styles.innerWrapper)}>{renderStreetFields()}</div>
            {renderStreetAdditionalField()}
          </>
        )}
      </div>
    );
  }
);
